import "./TopupPlansDialog.scss";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Coins, Loader2, AlertCircle, Check } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { IRootReducerState } from "@/store/reducers";
import { useEffect, useState, useCallback } from "react";
import { getTopupPlans } from "@/store/actions/meta.action";
import { ITopupPlanModel } from "@/store/reducers/meta.reducer";
import { CommonService } from "@/services";
import { getUsersTokensUtilization } from "@/store/actions/meta.action";
import ENV from "@/constants/ENV";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

// Extend Window interface for Razorpay
declare global {
    interface Window {
        Razorpay: any;
    }
}

interface TopupPlansDialogProps {
    isOpen: boolean;
    onClose: () => void;
}

const TopupPlansDialog = ({ 
    isOpen, 
    onClose, 
}: TopupPlansDialogProps) => {
    
    const dispatch = useDispatch();
    
    // Get topup plans data from Redux store
    const topupPlans = useSelector((state: IRootReducerState) => state.meta.topupPlans);
    const topupPlansLoading = useSelector((state: IRootReducerState) => state.meta.topupPlansLoading);
    const topupPlansError = useSelector((state: IRootReducerState) => state.meta.topupPlansError);
    const user = useSelector((state: IRootReducerState) => state.auth.user);
    
    // State for payment processing
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
    
    
    // Helper function to get plan price based on region
    const getPlanPrice = useCallback((plan: ITopupPlanModel) => {
        const costStructure = plan.plan_details.cost_structure;
        // Use is_domestic to determine currency: true = INR (India), false = USD (US/Global)
        const regionCost = plan.is_domestic ? costStructure.IN : (costStructure.US || costStructure.GLOBAL);
        return {
            amount: regionCost.amount,
            currency: regionCost.currency
        };
    }, []);
    
    
    // Fetch topup plans when dialog opens
    useEffect(() => {
        if (isOpen && (!topupPlans || topupPlans.length === 0)) {
            dispatch(getTopupPlans());
        }
        // Auto-select first plan when dialog opens and plans are available
        if (isOpen && topupPlans && topupPlans.length > 0 && !selectedPlanId) {
            setSelectedPlanId(topupPlans[0].id);
        }
    }, [isOpen, dispatch, topupPlans, selectedPlanId]);
    
    
    // Handle Razorpay payment
    const handleRazorpayPayment = useCallback(async (orderId: string) => {
        try {
            const options = {
                key: ENV.RAZORPAY_KEY ,
                order_id: orderId,
                name: 'SuperEngineer',
                description: 'Top-up Credits',
                handler: function () {
                    setIsLoading(true);
                    CommonService._alert.showToast("Payment successful", "success");
                    CommonService.handleMixpanelTrack("Topup Payment Successful", {
                        user_id: user?.id,
                        plan_id: selectedPlanId,
                        timestamp: new Date().toISOString()
                    });
                    // Add delay before checking transaction
                    setTimeout(() => {
                        dispatch(getUsersTokensUtilization());
                    }, 500);
                },
                prefill: {
                    name: user?.name,
                    email: user?.email,
                    contact: ''
                },
                theme: {
                    color: '#3399cc'
                }
            };
            const rzp = new window.Razorpay(options);
            rzp.open();
        } catch (error) {
            console.error('Error opening Razorpay:', error);
            CommonService._alert.showToast("Failed to open payment gateway", "error");
        }
    }, [user, dispatch, selectedPlanId]);
    
    // Generate topup transaction and initiate payment
    const generateTopupTransaction = useCallback((plan: ITopupPlanModel) => {
        const payload = {
            plan_id: plan.id
        };
        
        setIsProcessingPayment(true);
        
        CommonService._payments_Service.createTopupTransaction(payload)
            .then((response: any) => {
                const transactionId = response?.data?.transaction_id;
                const rzTransactionId = response?.data?.rz_transaction_id;

                CommonService.handleMixpanelTrack("Topup Payment Initiated", {
                    user_id: user?.id,
                    plan_id: plan.id,
                    timestamp: new Date().toISOString()
                });
                
                if (transactionId && rzTransactionId) {
                    setIsProcessingPayment(false);
                    onClose(); 
                    handleRazorpayPayment(rzTransactionId);
                    // Keep dialog open, reset processing state after payment gateway opens
                } else {
                    throw new Error("Transaction IDs not received");
                }
            })
            .catch((error: any) => {
                CommonService.handleMixpanelTrack("Topup Payment Failed", {
                    user_id: user?.id,
                    plan_id: plan.id,
                    error_message: error?.message,
                    timestamp: new Date().toISOString()
                });
                CommonService._alert.showToast(
                    error?.message || "Failed to initiate payment. Please try again.",
                    "error"
                );
                setIsProcessingPayment(false);
            });
    }, [handleRazorpayPayment, onClose,user]);
    
    
    // Get selected plan details
    const selectedPlan = topupPlans?.find(p => p.id === selectedPlanId);
    const selectedPlanPrice = selectedPlan ? getPlanPrice(selectedPlan) : null;
    
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="topup-dialog-container">
                <DialogHeader className="topup-dialog-header">
                    <DialogTitle className="topup-dialog-title">
                        <Coins className="h-5 w-5 mr-2" />
                        Buy Credits
                    </DialogTitle>
                </DialogHeader>
                
                <div className="topup-dialog-content">
                    {topupPlansLoading ? (
                        <div className="loading-state">
                            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-3" />
                            <p className="text-center text-sm text-muted-foreground">Loading top-up plans...</p>
                        </div>
                    ) : topupPlansError ? (
                        <div className="error-state">
                            <AlertCircle className="h-6 w-6 text-destructive mx-auto mb-3" />
                            <p className="text-center text-sm text-destructive">Failed to load top-up plans</p>
                            <p className="text-center text-xs text-muted-foreground mb-4">{topupPlansError}</p>
                            <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => dispatch(getTopupPlans())}
                                className="mx-auto"
                            >
                                Try Again
                            </Button>
                        </div>
                    ) : topupPlans && topupPlans.length > 0 ? (
                        <div className="topup-plans-content">
                            {/* Plan Selector Dropdown */}
                            <div className="plan-selector-section">
                                <label className="selector-label">Select Credit Package</label>
                                <Select value={selectedPlanId || ''} onValueChange={setSelectedPlanId}>
                                    <SelectTrigger className="plan-select-trigger">
                                        <SelectValue placeholder="Choose a plan" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectGroup>
                                            {topupPlans.map((plan) => {
                                                const price = getPlanPrice(plan);
                                                return (
                                                    <SelectItem key={plan.id} value={plan.id}>
                                                        {plan.plan_name} - {price.currency} {price.amount}
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectGroup>
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Selected Plan Details Card */}
                            {selectedPlan && selectedPlanPrice && (
                                <div className="selected-plan-card">
                                    <div className="plan-card-header">
                                        <div className="plan-card-title">
                                            <Coins className="plan-card-icon" />
                                            <h3 className="plan-card-name">{selectedPlan.plan_name}</h3>
                                        </div>
                                        <div className="plan-card-price">
                                            <span className="price-currency">{selectedPlanPrice.currency}</span>
                                            <span className="price-amount">{selectedPlanPrice.amount}</span>
                                        </div>
                                    </div>

                                    <div className="plan-card-description">
                                        {selectedPlan.plan_details.description}
                                    </div>

                                    <div className="plan-card-features">
                                        <div className="features-header">What's included:</div>
                                        {selectedPlan.plan_details.features.map((feature, index) => (
                                            <div key={index} className="feature-item">
                                                <Check className="feature-check-icon" />
                                                <span className="feature-text">{feature}</span>
                                            </div>
                                        ))}
                                    </div>

                                    <Button 
                                        className="purchase-button"
                                        onClick={() => {
                                            if (selectedPlan) {
                                                generateTopupTransaction(selectedPlan);
                                            }
                                        }}
                                        disabled={isProcessingPayment || isLoading}
                                        size="lg"
                                    >
                                        {isProcessingPayment ? (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                                Processing...
                                            </>
                                        ) : isLoading ? (
                                            "Processing Payment..."
                                        ) : (
                                            <>
                                                <Coins className="h-4 w-4 mr-2" />
                                                Purchase Credits
                                            </>
                                        )}
                                    </Button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="empty-state">
                            <Coins className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
                            <p className="text-center text-sm text-muted-foreground">No top-up plans available</p>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default TopupPlansDialog;
